import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { PartnershipRequest } from "../../../../core/types/types";
import apiInstance from "../../../../utility/apiInstance";
import CustomSelect from "../../../../core/common/commonSelect";
import { Country } from "../../../../core/common/selectOption/json/selectOption";
import { ALPHA_REGEX, stripNonAlpha, PHONE_REGEX, stripNonNumeric } from "../../../../utility/inputValidators";

const Form = () => {
    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: "",
    });

    const [errorField, setErrorField] = useState('');

    const [formData, setFormData] = useState<PartnershipRequest>({
        institutionName: "",
        country: "",
        contactFirstName: "",
        contactLastName: "",
        contactEmail: "",
        contactPhone: "",
        contactTitle: "",
        comments: "",
        status: "pending",
    });

    const handleInputChange = (e: any) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === 'contactFirstName' || name === 'contactLastName') {
            value = stripNonAlpha(value);
        } else if (name === 'contactPhone') {
            value = stripNonNumeric(value);
        }
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        if (errorField === name) setErrorField('');
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validate = () => {
        let newErrors = "";
        let invalidField = "";

        if (!formData.institutionName?.trim()) {
            newErrors = "School name is required";
            invalidField = 'institutionName';
        } else if (!formData.country?.trim()) {
            newErrors = "Destination country is required";
            invalidField = 'country';
        } else if (!formData.contactFirstName?.trim() || !ALPHA_REGEX.test(formData.contactFirstName)) {
            newErrors = "Valid contact first name is required";
            invalidField = 'contactFirstName';
        } else if (!formData.contactLastName?.trim() || !ALPHA_REGEX.test(formData.contactLastName)) {
            newErrors = "Valid contact last name is required";
            invalidField = 'contactLastName';
        } else if (
            !formData.contactEmail?.trim() ||
            !emailRegex.test(formData.contactEmail)
        ) {
            newErrors = "Valid email is required";
            invalidField = 'contactEmail';
        } else if (
            !formData.contactPhone?.trim() ||
            !PHONE_REGEX.test(formData.contactPhone)
        ) {
            newErrors = "Valid phone number is required";
            invalidField = 'contactPhone';
        } else if (!formData.contactTitle?.trim()) {
            newErrors = "Contact title is required";
            invalidField = 'contactTitle';
        }

        setErrorField(invalidField);

        setMsg({
            error: true,
            loading: false,
            text: newErrors,
        });

        return newErrors.length === 0;
    };

    const submitRequest = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        setMsg({
            error: false,
            loading: true,
            text: "",
        });

        try {
            const response = await apiInstance.post(
                `/api/partnership-requests`,
                formData
            );

            setFormData({
                institutionName: "",
                country: "",
                contactFirstName: "",
                contactLastName: "",
                contactEmail: "",
                contactPhone: "",
                contactTitle: "",
                comments: "",
                status: "pending",
            });

            setMsg({
                error: false,
                loading: false,
                text: "Request submitted successfully!",
            });
        } catch (error: any) {
            setMsg({
                error: true,
                loading: false,
                text: error.message,
            });
        }
    };

    return (
        <>
            <section className="m-5" id="partnership-form">
                <div className="col-lg-12">
                    <div className="card mb-0">
                        <div className="card-body p-4 p-sm-5 p-md-6">
                            <h4 className="mb-3">Partnership Request</h4>
                            <form onSubmit={submitRequest}>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                School Name{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errorField === 'institutionName' ? 'is-invalid' : ''}`}
                                                name="institutionName"
                                                value={formData.institutionName}
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'institutionName' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Destination Country{" "}
                                                <span className="text-danger">
                                                    {" "}
                                                    *
                                                </span>
                                            </label>
                                            <CustomSelect
                                                options={Country}
                                                className={`select d-flex ${errorField === 'country' ? 'is-invalid' : ''}`}
                                                placeholder="Select"
                                                name="country"
                                                value={formData.country}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: "country",
                                                            value: selectedOption.value,
                                                        },
                                                    });
                                                }}
                                            />
                                            {errorField === 'country' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                First Name{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errorField === 'contactFirstName' ? 'is-invalid' : ''}`}
                                                name="contactFirstName"
                                                value={
                                                    formData.contactFirstName
                                                }
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'contactFirstName' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Last Name{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errorField === 'contactLastName' ? 'is-invalid' : ''}`}
                                                name="contactLastName"
                                                value={formData.contactLastName}
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'contactLastName' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Email{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="email"
                                                className={`form-control ${errorField === 'contactEmail' ? 'is-invalid' : ''}`}
                                                name="contactEmail"
                                                value={formData.contactEmail}
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'contactEmail' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Phone No.{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="tel"
                                                className={`form-control ${errorField === 'contactPhone' ? 'is-invalid' : ''}`}
                                                name="contactPhone"
                                                value={formData.contactPhone}
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'contactPhone' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Title{" "}
                                                <span className="ms-1 text-danger">
                                                    *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errorField === 'contactTitle' ? 'is-invalid' : ''}`}
                                                name="contactTitle"
                                                value={formData.contactTitle}
                                                onChange={handleInputChange}
                                            />
                                            {errorField === 'contactTitle' && (
                                                <div className="invalid-feedback d-block">{msg.text}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="form-label">
                                        Any additional comments
                                    </label>
                                    <textarea
                                        className="form-control form-control-lg"
                                        rows={4}
                                        name="comments"
                                        value={formData.comments}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="d-grid col-lg-3">
                                    <button
                                        type="submit"
                                        className="btn btn-secondary btn-lg"
                                    >
                                        Submit
                                    </button>
                                    {msg.loading && (
                                        <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                                            Submitting request...
                                        </span>
                                    )}
                                    {!msg.loading && !errorField && (
                                        <span
                                            className={`mt-3 ${msg.error ? "text-red" : ""} fs-14 fw-normal d-flex align-items-center justify-content-center`}
                                        >
                                            {msg.text}
                                        </span>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Form;
